import { useEffect, useRef, useState } from 'react';
import i18next from '@/lib/utils/i18n';
import { isRetracedStaff } from '@/lib/utils/managementAccess';
import {
  startRecordingTranslationKeys,
  stopRecordingTranslationKeys,
} from '@/lib/utils/translationKeyRecorder';
import { useSessionStore } from '@/store/sessionStore';
import { inspectElement, isInspectorUi } from '../inspectElement';
import type { InspectedTarget } from '../types';

/**
 * Staff only, the operator behind an impersonated session included: the
 * translation endpoints are sent as the operator (`asOperator`).
 */
export function useTranslationInspector() {
  const user = useSessionStore(s => s.user);
  const canInspect =
    isRetracedStaff(user) || isRetracedStaff(user?.unImpersonatedUser);

  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState<InspectedTarget | null>(null);
  const [selected, setSelected] = useState<string[] | null>(null);
  // Read by the listeners, which are attached once per activation.
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    if (!active) return;
    startRecordingTranslationKeys(i18next);

    let frame = 0;
    // The page under an open panel is left alone: the panel handles its own
    // pointer and Escape.
    const passes = (event: Event) =>
      selectedRef.current !== null || isInspectorUi(event.target);

    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setHovered(passes(event) ? null : inspectElement(event.target)),
      );
    };
    // Picking a label must not also press the button it sits on. Pointer and
    // mouse down too: menus and selects open on those, not on click.
    const swallow = (event: Event) => {
      if (passes(event)) return;
      event.preventDefault();
      event.stopPropagation();
    };
    const onClick = (event: MouseEvent) => {
      if (passes(event)) return;
      swallow(event);
      const target = inspectElement(event.target);
      if (target) {
        setHovered(null);
        setSelected(target.keys);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedRef.current === null) {
        setActive(false);
      }
    };

    const listeners: [string, EventListener][] = [
      ['pointermove', onMove as EventListener],
      ['pointerdown', swallow],
      ['mousedown', swallow],
      ['mouseup', swallow],
      ['click', onClick as EventListener],
      ['keydown', onKeyDown as EventListener],
    ];
    for (const [type, listener] of listeners) {
      document.addEventListener(type, listener, true);
    }
    return () => {
      for (const [type, listener] of listeners) {
        document.removeEventListener(type, listener, true);
      }
      cancelAnimationFrame(frame);
      stopRecordingTranslationKeys(i18next);
      setHovered(null);
    };
  }, [active]);

  return {
    canInspect,
    active,
    toggle: () => setActive(current => !current),
    hovered,
    selected,
    closePanel: () => setSelected(null),
  };
}
